import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { useOrganizations } from '../../hooks/useOrganizationData';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './Settings.css';

const OrganizationManagement = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isSuperAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const notification = useNotification();

  const { data: organizations = [], isLoading, refetch: refetchOrganizations } =
    useOrganizations({ enabled: isSuperAdmin() });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(null);

  useEffect(() => {
    if (!isUserLoading && !isSuperAdmin()) {
      notification.error('Only Super Admins can access this page');
      navigate('/dashboard');
    }
  }, [isSuperAdmin, isUserLoading, navigate, notification]);

  const handleCreateClick = () => {
    navigate('/settings/organizations/new');
  };

  const handleEditClick = (org) => {
    navigate(`/settings/organizations/${org._id || org.id}`);
  };

  const handleDeleteClick = (org) => {
    setDeletingOrg(org);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingOrg) return;

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/organizations/${deletingOrg._id || deletingOrg.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notification.success('Organization deleted successfully');
      setIsDeleteModalOpen(false);
      setDeletingOrg(null);
      refetchOrganizations();
    } catch (error) {
      console.error('Error deleting organization:', error);
      notification.error(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  if (isUserLoading || isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-page-header">
          <div className="settings-page-header-left">
            <h1 className="settings-page-title">All Organizations</h1>
            <p className="settings-page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="settings-page-content">
          <div className="settings-card">
            <div className="settings-loading">Loading organizations...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <ConfirmDialog
        open={isDeleteModalOpen}
        title="Delete Organization"
        description={`Are you sure you want to delete "${deletingOrg?.name}"? This action cannot be undone. All users, locations, products, and flyers associated with this organization will be permanently deleted.`}
        onConfirm={handleDelete}
        onCancel={() => { setIsDeleteModalOpen(false); setDeletingOrg(null); }}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="settings-page-header">
        <div className="settings-page-header-left">
          <h1 className="settings-page-title">All Organizations</h1>
          <p className="settings-page-subtitle">Manage all organizations in the platform</p>
        </div>
        <div className="settings-page-header-right">
          <button className="settings-btn secondary" onClick={() => navigate('/settings/admin-tokens')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Admin Tokens
          </button>
          <button className="settings-btn primary" onClick={handleCreateClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Create New Organization
          </button>
        </div>
      </div>

      <div className="settings-page-content">
        <div className="settings-card">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Users</th>
                <th>Locations</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => {
                const orgId = org._id || org.id;
                return (
                  <tr key={orgId}>
                    <td>
                      <span className="settings-table-primary">{org.name}</span>
                    </td>
                    <td>{org.email || '-'}</td>
                    <td>{org.phone || '-'}</td>
                    <td>
                      <span className="settings-badge">{org.user_count || 0}</span>
                    </td>
                    <td>
                      <span className="settings-badge">{org.location_count || 0}</span>
                    </td>
                    <td>
                      {org.created_at
                        ? new Date(org.created_at).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td>
                      <div className="settings-table-actions">
                        <button
                          className="settings-action-btn primary"
                          onClick={() => handleEditClick(org)}
                          title="Edit organization"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          className="settings-action-btn danger"
                          onClick={() => handleDeleteClick(org)}
                          title="Delete organization"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {organizations.length === 0 && (
                <tr>
                  <td colSpan="7" className="settings-table-empty">
                    No organizations found. Click "Create New Organization" to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrganizationManagement;
